import type { UseCase } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { LayoutGrid, Map as MapIcon, Search, Sparkles, Table2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { z } from "zod";
import { SuccessRate } from "@/components/domain/analytics";
import { PageHeader } from "@/components/domain/misc";
import { Page, QueryState } from "@/components/layout/page";
import { Card, CardHeader } from "@/components/ui/card";
import { Input, Select } from "@/components/ui/input";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { Segmented } from "@/features/analytics/bits";
import { KpiTile } from "@/features/overview/kpi-tile";
import { Callout } from "@/features/profile/review-step";
import { type UseCaseAction, UseCaseDialogs } from "@/features/use-cases/use-case-actions";
import { UseCaseTree } from "@/features/use-cases/use-case-tree";
import { SessionMapView } from "@/features/use-cases/session-map";
import { UseCaseTreemap } from "@/features/use-cases/use-case-treemap";
import { q } from "@/lib/api";
import { fmtCompact, fmtPct } from "@/lib/format";

const searchSchema = z.object({
  view: z.enum(["table", "treemap", "map"]).optional().catch(undefined),
  agentId: z.string().min(1).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/use-cases/")({
  validateSearch: searchSchema,
  component: UseCasesPage,
});

function UseCasesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const useCases = useQuery(q.useCases(search.agentId ? { agentId: search.agentId } : {}));
  const agents = useQuery(q.agents());
  const view = search.view ?? "table";

  return (
    <Page wide>
      <PageHeader
        title="Use cases"
        description="What users come to your agents for, discovered nightly from session intents. Confirm, rename, merge or hide to curate the map; confirmed use cases keep their ids, so trends don't reset."
      />
      <QueryState
        query={useCases}
        loading={
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-[104px]" />
              ))}
            </div>
            <Skeleton className="h-[480px]" />
          </div>
        }
      >
        {(list) => (
          <UseCaseMap
            useCases={list}
            agentNames={new Map(agents.data?.map((a) => [a.id, a.name]))}
            toolbar={
              <>
                <div className="relative w-56">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-3" aria-hidden />
                  <Input aria-label="Filter use cases by name" placeholder="Filter by name…" value={search.q ?? ""} onChange={(e) => setSearch({ q: e.target.value || undefined })} className="pl-8" />
                </div>
                <Select aria-label="Filter by agent" value={search.agentId ?? ""} onChange={(e) => setSearch({ agentId: e.target.value || undefined })} className="w-44">
                  <option value="">All agents</option>
                  {agents.data?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
                <Segmented
                  label="Map view"
                  value={view}
                  onChange={(v) => setSearch({ view: v === "table" ? undefined : v })}
                  options={[
                    { value: "table", label: "Table", icon: <Table2 /> },
                    { value: "treemap", label: "Treemap", icon: <LayoutGrid /> },
                    { value: "map", label: "Map", icon: <MapIcon /> },
                  ]}
                />
              </>
            }
            view={view}
            agentId={search.agentId}
            filter={search.q}
            onClearFilter={() => setSearch({ q: undefined })}
          />
        )}
      </QueryState>
    </Page>
  );
}

function UseCaseMap({ useCases, agentNames, toolbar, view, agentId, filter, onClearFilter }: { useCases: UseCase[]; agentNames: Map<string, string>; toolbar: ReactNode; view: "table" | "treemap" | "map"; agentId?: string; filter?: string; onClearFilter: () => void }) {
  const overview = useQuery(q.analyticsOverview());
  const [target, setTarget] = useState<{ action: UseCaseAction; useCase: UseCase } | null>(null);
  const needle = filter?.trim().toLowerCase();
  // A name filter keeps matching children together with their parent so the hierarchy stays readable.
  const shown = needle
    ? useCases.filter((u) => {
        const hit = (x: UseCase) => x.name.toLowerCase().includes(needle) || x.description.toLowerCase().includes(needle);
        return hit(u) || useCases.some((c) => c.parentId === u.id && hit(c)) || (u.parentId ? useCases.some((p) => p.id === u.parentId && hit(p)) : false);
      })
    : useCases;
  const roots = useCases.filter((u) => !u.parentId);
  const discovered = useCases.filter((u) => u.status === "discovered");
  const worst = [...useCases].filter((u) => u.sessions7d >= 100).sort((a, b) => a.successRate - b.successRate)[0];
  const discoveredSessions = discovered.reduce((n, u) => n + u.sessions7d, 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiTile label="Use cases" value={useCases.length} sub={`${roots.length} top-level · ${useCases.length - roots.length} sub-cases`} />
        <KpiTile label="Awaiting review" value={discovered.length} sub={discovered.length ? `${fmtCompact(discoveredSessions)} sessions in discovered use cases` : "Every use case is confirmed"} />
        <KpiTile
          label="Lowest success"
          to={worst ? `/use-cases/${worst.id}` : undefined}
          value={worst ? <SuccessRate value={worst.successRate} /> : "—"}
          sub={worst ? <span className="block truncate">{worst.name}</span> : "Not enough volume yet"}
        />
        <KpiTile
          label="Unclassified intents"
          value={overview.data ? fmtPct(overview.data.unclassifiedShare, 1) : <Skeleton className="h-6 w-12" />}
          sub="of sessions matched no use case; re-cut at 5%"
        />
      </div>

      {discovered.length ? (
        <Callout tone="info" icon={<Sparkles />} title={`${discovered.length} discovered ${discovered.length === 1 ? "use case needs" : "use cases need"} review`}>
          {discovered
            .slice(0, 4)
            .map((u) => u.name)
            .join(", ")}
          {discovered.length > 4 ? ` and ${discovered.length - 4} more` : ""}. Confirm them to lock their ids, or merge them into an existing use case if discovery split one intent in two.
        </Callout>
      ) : null}

      <Card>
        <CardHeader
          title="Use-case map"
          description={view === "table" ? "Parents expand to sub-cases. Sorting applies within each level." : view === "map" ? "Recent sessions clustered by what users asked; nearby dots are similar conversations." : "Area is volume, color is success rate."}
          actions={<div className="flex flex-wrap items-center gap-2">{toolbar}</div>}
        />
        {view === "map" ? (
          <SessionMapView agentId={agentId} />
        ) : needle && !shown.length ? (
          <EmptyState
            icon={<Search />}
            title={`No use case matches “${filter}”`}
            action={
              <button type="button" onClick={onClearFilter} className="cursor-pointer text-xs text-accent hover:underline">
                Clear the filter
              </button>
            }
          />
        ) : view === "treemap" ? (
          <div className="p-4">
            <UseCaseTreemap useCases={shown} />
          </div>
        ) : (
          <UseCaseTree useCases={shown} agentNames={agentNames} onAction={(action, useCase) => setTarget({ action, useCase })} />
        )}
      </Card>
      <UseCaseDialogs target={target} all={useCases} onClose={() => setTarget(null)} />
    </div>
  );
}
